import type { ConsoleEvalCase, ConsoleTestAccount } from '@plato/shared';
import { useState } from 'react';
import { ActionError, ReadOnlyNote, useAction } from '../../components/action.tsx';
import { DataTable } from '../../components/data-table.tsx';
import { formatDate } from '../../components/format.ts';
import { Loaded } from '../../components/page.tsx';
import { useConsole, useHasRole, useReloadableTenantQuery } from '../../data/console-context.tsx';
import type { ConsoleData, TenantSummary } from '../../data/console-data.ts';
import { CaseForm } from './case-form.tsx';
import { OutcomeChip, outcomeOf } from './outcome.tsx';

const loadCases = async (data: ConsoleData, tenant: TenantSummary) => {
  const [cases, accounts] = await Promise.all([
    data.listEvalCases(tenant.id),
    data.listTestAccounts(tenant.id),
  ]);
  return { cases, accounts };
};

export function CasesPanel(props: { onRunStarted: (runId: string) => void }) {
  const { query, reload } = useReloadableTenantQuery(loadCases);
  const canEdit = useHasRole('editor');
  const [editing, setEditing] = useState<ConsoleEvalCase | 'new' | null>(null);
  return (
    <section>
      {!canEdit && <ReadOnlyNote needs="editor" />}
      <Loaded query={query}>
        {({ cases, accounts }) =>
          editing !== null ? (
            <CaseForm
              key={editing === 'new' ? 'new' : editing.id}
              existing={editing === 'new' ? null : editing}
              accounts={accounts}
              onDone={(saved) => {
                setEditing(null);
                if (saved) reload();
              }}
            />
          ) : (
            <CaseList
              cases={cases}
              accounts={accounts}
              canEdit={canEdit}
              onNew={() => setEditing('new')}
              onEdit={setEditing}
              onChanged={reload}
              onRunStarted={props.onRunStarted}
            />
          )
        }
      </Loaded>
    </section>
  );
}

function CaseList(props: {
  cases: readonly ConsoleEvalCase[];
  accounts: readonly ConsoleTestAccount[];
  canEdit: boolean;
  onNew: () => void;
  onEdit: (evalCase: ConsoleEvalCase) => void;
  onChanged: () => void;
  onRunStarted: (runId: string) => void;
}) {
  const { data, tenant } = useConsole();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const action = useAction();
  const runnable = props.cases.filter((entry) => !entry.needs_rubric);
  const accountName = (id: string | null) =>
    id === null
      ? 'None (no vendor tools)'
      : (props.accounts.find((account) => account.id === id)?.name ?? 'Removed');
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const start = (caseIds?: readonly string[]) =>
    void action.run(async () => {
      const run = await data.startEvalRun(tenant.id, caseIds);
      props.onRunStarted(run.id);
    });

  return (
    <>
      {props.canEdit && (
        <div className="row wrap">
          <button type="button" onClick={props.onNew}>
            New case
          </button>
          <button
            type="button"
            className="primary"
            disabled={action.pending || runnable.length === 0}
            onClick={() => start()}
          >
            Run all ({runnable.length})
          </button>
          <button
            type="button"
            disabled={action.pending || selected.size === 0}
            onClick={() => start([...selected])}
          >
            Run selected ({selected.size})
          </button>
        </div>
      )}
      <ActionError error={action.error} />
      <DataTable
        caption="Cases"
        columns={[
          ...(props.canEdit
            ? [
                {
                  header: 'Select',
                  cell: (row: ConsoleEvalCase) => (
                    <input
                      type="checkbox"
                      aria-label={`Select ${row.name}`}
                      disabled={row.needs_rubric}
                      checked={selected.has(row.id)}
                      onChange={() => toggle(row.id)}
                    />
                  ),
                },
              ]
            : []),
          { header: 'Name', cell: (row) => row.name },
          { header: 'Question', cell: (row) => <span className="muted">{row.question}</span> },
          {
            header: 'Rubric',
            cell: (row) =>
              row.needs_rubric ? (
                <span className="chip chip-warning">Needs a rubric</span>
              ) : (
                row.rubric
              ),
          },
          { header: 'Test account', cell: (row) => accountName(row.test_account_id) },
          {
            header: 'Source',
            cell: (row) => (row.source === 'conversation' ? 'A conversation' : 'Written here'),
          },
          {
            header: 'Last result',
            cell: (row) =>
              row.last_result === null ? (
                <span className="muted">Not run</span>
              ) : (
                <span className="row">
                  <OutcomeChip status={outcomeOf(row.last_result)} />
                  <span className="muted">{formatDate(row.last_result.at)}</span>
                </span>
              ),
          },
          ...(props.canEdit
            ? [
                {
                  header: 'Actions',
                  cell: (row: ConsoleEvalCase) => (
                    <span className="row">
                      <button type="button" className="quiet" onClick={() => props.onEdit(row)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="danger"
                        disabled={action.pending}
                        onClick={() =>
                          void action
                            .run(() => data.archiveEvalCase(tenant.id, row.id))
                            .then((ok) => ok && props.onChanged())
                        }
                      >
                        Archive
                      </button>
                    </span>
                  ),
                },
              ]
            : []),
        ]}
        rows={props.cases}
      />
      {props.cases.length === 0 && (
        <p className="muted">
          No cases yet. Write one here, or save a question from a conversation your users had.
        </p>
      )}
    </>
  );
}
